import React, { createContext, useEffect, useReducer } from 'react';

// Applied to all fields
const variant = 'standard';
const margin = 'normal';

export declare type ValidationSchema = Record<
  string,
  {
    value?: any
    error?: string
    required?: boolean
    validate?:
      | 'text'
      | 'number'
      | 'email'
      | 'phone'
      | 'zip'
      | 'checkbox'
      | 'select'
    minLength?: number
    maxLength?: number
    helperText?: string
  }
>

type ContextProps = {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  setStep: (step: number) => void;
  removeLocalStorage: () => void;
  variant: 'outlined' | 'standard' | 'filled';
  margin: 'dense' | 'normal' | 'none';
}

export const AppContext = createContext<ContextProps>({
  activeStep: parseInt(localStorage.getItem('activestep') || '0') || 0,
  handleNext() {},
  handleBack() {},
  setStep() {},
  removeLocalStorage() {},
  variant,
  margin
})

interface ProviderProps {
  children: React.ReactNode;
}

type State = {
  activeStep: number;
}

type Action =
  | { type: 'increase'; }
  | { type: 'decrease'; }
  | { type: 'set-step'; step: number; }
  | { type: 'form-value'; name: string; fieldValue: any; }
  | { type: 'form-error'; name: string; error: string; }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'increase':
      return {
        ...state,
        activeStep: state.activeStep + 1
      };

    case 'decrease':
      return {
        ...state,
        activeStep: state.activeStep - 1
      };

    case 'set-step':
      return {
        ...state,
        activeStep: action.step
      };

    case 'form-error':
      return {
        ...state,
      };
      
    default:
      return state;
  }
}

export function StepsProvider({ children }: ProviderProps) {
  const [{ activeStep }, dispatch] = useReducer(reducer, {
    activeStep: parseInt(localStorage.getItem('activestep') || '0') || 0
  });

  // Proceed to next step
  const handleNext = () => dispatch({ type: 'increase' });

  // Go back to prev step
  const handleBack = () => dispatch({ type: 'decrease' });

  // Set step
  const setStep = (step: number) => dispatch({type: 'set-step', step});

  // Remove active step from localStorage
  const removeLocalStorage = () => localStorage.removeItem('activestep');

  useEffect(() => {
    localStorage.setItem('activestep', activeStep.toString());

    if (activeStep === 3) {
      removeLocalStorage();
    }
  }, [activeStep]);

  return (
    <AppContext.Provider
      value={{
        activeStep,
        handleNext,
        handleBack,
        setStep,
        removeLocalStorage,
        variant,
        margin
      }}
    >
      <div className="mui-step-form">{children}</div>
    </AppContext.Provider>
  )
}
